/**
 * Connection status indicator component.
 * Visual indicator for connector connection status.
 */

// React Imports
import { Chip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import type { ChipColor } from '@coordio/ui';
import {
  CheckCircle as CheckCircleIcon,
  CircleAlert as ErrorIcon,
  AlertTriangle as WarningIcon,
  CircleHelp as HelpOutlineIcon,
} from 'lucide-react';

export type ConnectionStatus = 'Connected' | 'Disconnected' | 'Expired' | 'Error' | 'Unknown';

export interface ConnectionStatusIndicatorProps {
  status: ConnectionStatus;
  message?: string;
  size?: 'small' | 'medium';
}

/**
 * Connection status indicator component.
 */
export const ConnectionStatusIndicator = ({
  status,
  message,
  size = 'small',
}: ConnectionStatusIndicatorProps) => {
  const getStatusConfig = (): { color: ChipColor; icon: React.ReactNode; label: string } => {
    const iconSize = size === 'small' ? 14 : 18;
    switch (status) {
      case 'Connected':
        return {
          color: 'green',
          icon: <CheckCircleIcon size={iconSize} />,
          label: 'Connected',
        };
      case 'Disconnected':
        return {
          color: 'default',
          icon: <HelpOutlineIcon size={iconSize} />,
          label: 'Disconnected',
        };
      case 'Expired':
        return {
          color: 'amber',
          icon: <WarningIcon size={iconSize} />,
          label: 'Expired',
        };
      case 'Error':
        return {
          color: 'red',
          icon: <ErrorIcon size={iconSize} />,
          label: 'Error',
        };
      default:
        return {
          color: 'default',
          icon: <HelpOutlineIcon size={iconSize} />,
          label: 'Unknown',
        };
    }
  };

  const config = getStatusConfig();

  const chip = (
    <Chip
      label={config.label}
      icon={config.icon}
      color={config.color}
      variant="outlined"
    />
  );

  if (message) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {chip}
          </TooltipTrigger>
          <TooltipContent>{message}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return chip;
};
